import React, { useCallback, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { observer } from 'mobx-react-lite';
import { CommentModel } from '../../../../../mobx/models/comment';
import { CommentFilesInterface, TaskCommentProps } from '../../../../../interfaces/TaskInterface';
import { ReplyMessageDataInterface } from '../../../../../interfaces/MessageInterface';
import TaskActivity from '../taskActivity';
import useKeyboardVisibility from '../../../../../global/useKeyboard';
import { captureException } from '../../../../../services/logService';
import {
  getFirebaseUrlFromFile,
  convertBase64ToFile,
  fetchAndConvertToBase64,
  isEmpty,
  isEmptyMessage,
  getRawComment,
  buildCommentUploadFolder
} from '../../../../../helpers/common';
import { createNewComment, updateTaskComment } from '../../../../../services/taskServices';
import { trackAnalyticActivity } from '../../../../../services/analyticsService';
import { TASK_ANALYTICS } from '../../../../../global/analyticsConstants';
import DraftMessageCacheService from '../../../../../services/draftMessageCacheService';
import { COMMENT_TYPE, DRAFT_MESSAGE_TYPE } from '../../../../../global/constants';
import { rootStore } from '../../../../../mobx/rootStore';
import { nanoid } from 'nanoid';
import UserPreferenceSingleton from '../../../../../helpers/userPreferenceSingleton';

const TaskComments = observer(
  forwardRef(function TaskComments(props: TaskCommentProps, ref) {
    const {
      taskId,
      taskDetails,
      attachmentPage,
      handleOpenSnapModal,
      onClickHandleRefreshFigma,
      onClickHandleRichtextClick,
      handleActivityFileCard,
      figmaRefreshLoading,
      inlineRef,
      isOpenComment,
      setIsOpenComment,
      comment,
      setComment,
      uploadedFiles,
      setUploadedFiles,
      setAttachment,
      openDeleteCommentModal
    } = props;

    const [commentId, setCommentId] = useState<CommentModel | null>(null);
    const [editComment, setEditComment] = useState(false);
    const [replyMessage, setReplyMessage] = useState<ReplyMessageDataInterface>({});
    const [openEmoji, setOpenEmoji] = useState(false);
    const [selectedMessage, setSelectedMessage] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);

    const inlineScrollRef = useRef<HTMLDivElement>(null);
    const buttonDropdownRef = useRef<React.RefObject<HTMLDivElement>[]>([]);
    const emojiButtonRef = useRef<React.RefObject<HTMLDivElement>[]>([]);

    const isKeyboardVisible = useKeyboardVisibility();
    const currentUser = UserPreferenceSingleton.getInstance().getCurrentUser();
    const { workspaceStore } = rootStore;
    const { currentWorkspace: workspace } = workspaceStore;

    const processImageLinks = useCallback(
      async (commentDesc: string) => {
        let commentData = commentDesc;
        // Store comment images under the task's own folder when we know both ids; else fall back to `files/`.
        const commentUploadFolder = buildCommentUploadFolder(taskDetails?.projectId, taskId);
        const tempElement = document.createElement('div');
        tempElement.innerHTML = commentData || '';
        const linkElements = tempElement.querySelectorAll('div[src]');
        const promises: Promise<void>[] = [];
        linkElements.forEach((linkElement) => {
          const myPromise = new Promise<void>((resolve, reject) => {
            (async () => {
              try {
                const srclink = linkElement.getAttribute('src');
                if (srclink && srclink.startsWith('https://asana')) {
                  const base64Url = await fetchAndConvertToBase64(srclink);
                  const fileData = await convertBase64ToFile(base64Url);
                  const fileUrl = await getFirebaseUrlFromFile({ file: fileData }, commentUploadFolder);
                  if (fileUrl) {
                    const asanaUrlRegex = /https:\/\/asana[^\s"'<>)]*/g;
                    commentData = commentData.replace(asanaUrlRegex, fileUrl);
                  }
                } else if (
                  srclink &&
                  !srclink.startsWith('https://firebasestorage') &&
                  !srclink.startsWith('https://figma') &&
                  !srclink.startsWith('/static') &&
                  (!srclink.startsWith('https://') || srclink.startsWith('https://asana'))
                ) {
                  const fileData = await convertBase64ToFile(srclink);
                  const fileUrl = await getFirebaseUrlFromFile({ file: fileData }, commentUploadFolder);
                  if (fileUrl) {
                    commentData = commentData.replace(srclink, fileUrl);
                  }
                }
                resolve();
              } catch (error) {
                reject(error);
              }
            })();
          });
          promises.push(myPromise);
        });
        await Promise.all(promises);
        return commentData;
      },
      [taskDetails?.projectId, taskId]
    );

    const handleCommentSubmission = useCallback(
      async (commentData: string, uploadedFiles: CommentFilesInterface[], id: string) => {
        const updatedFiles = uploadedFiles.map(
          ({ __typename, isCreatedDoc, ...other }: CommentFilesInterface) => other
        );
        const replyMessagePayload = {
          CreatedTime: replyMessage?.CreatedTime,
          id: replyMessage?.id,
          Sender: replyMessage?.Sender,
          Sender_id: replyMessage?.Sender_id,
          Message: replyMessage?.Message,
          Files: replyMessage?.Files
        };
        const editorElement: HTMLElement | null = document.getElementById('inlineRTE_rte-edit-view');
        if (editorElement) {
          editorElement.innerHTML = '<p><br /></p>';
        }
        let result;
        let commentValue = '';
        setComment('');
        if (!commentId) {
          const newComment = {
            id,
            comment: commentData,
            Files: !isEmpty(updatedFiles) ? updatedFiles : [],
            type: COMMENT_TYPE.COMMENT,
            reply: replyMessage?.id,
            createdOn: new Date().toISOString(),
            createdBy: currentUser?.id,
            updatedBy: currentUser?.id,
            updatedTime: new Date().toISOString(),
            user: workspace?.users?.find((user) => user?.id === currentUser?.id),
            isArchived: false
          };
          commentValue = commentData;
          const updatedComment = getRawComment(newComment, currentUser);
          taskDetails?.upsertComment(updatedComment);
          setComment('');
          setUploadedFiles([]);
          inlineRef.current?.clearContent();
          setReplyMessage({});

          result = await createNewComment(taskId, {
            Comment: commentData,
            Files: !isEmpty(updatedFiles) ? updatedFiles : [],
            Type: COMMENT_TYPE.COMMENT,
            Reply: replyMessagePayload,
            id
          });
        } else {
          const newComment = {
            id: commentId?.id,
            createdOn: commentId?.createdOn.toISOString(),
            comment: commentData,
            Files: !isEmpty(updatedFiles) ? updatedFiles : [],
            type: COMMENT_TYPE.COMMENT,
            reply: commentId?.replyId || ''
          };
          commentValue = commentData;
          // Carry over what an edit does not change, else the comment on screen loses its
          // reactions and shows the editor as the author.
          const updatedComment = {
            ...getRawComment(newComment, currentUser),
            createdBy: commentId.createdById,
            reactions: commentId.reactions
          };
          taskDetails?.upsertComment(updatedComment);
          result = await updateTaskComment(taskId, commentId?.id, {
            Comment: commentData,
            Files: !isEmpty(updatedFiles) ? updatedFiles : []
          });
        }
        if (result) {
          trackAnalyticActivity(TASK_ANALYTICS.COMMENT_ADDED);
          setAttachment(false);
          setIsOpenComment(false);
          setEditComment(false);
          setReplyMessage({});
          if (editComment) {
            setUploadedFiles([]);
            setComment('');
            setCommentId(null);
          }
          await DraftMessageCacheService.getInstance()?.removeBulk([taskDetails?.id]);
        } else {
          const message = {
            id: taskDetails?.id,
            type: DRAFT_MESSAGE_TYPE.COMMENT,
            message: commentValue,
            file: uploadedFiles || []
          };
          await DraftMessageCacheService.getInstance()?.addBulk([message]);
        }
      },
      [
        commentId,
        editComment,
        currentUser,
        replyMessage?.CreatedTime,
        replyMessage?.Files,
        replyMessage?.Message,
        replyMessage?.Sender,
        replyMessage?.Sender_id,
        replyMessage?.id,
        taskDetails,
        taskId,
        workspace?.users,
        setComment,
        setUploadedFiles,
        inlineRef,
        setAttachment,
        setIsOpenComment
      ]
    );

    const onSendComment = useCallback(
      async (uploadedFiles: CommentFilesInterface[], commentDesc: string) => {
        try {
          setLoading(true);
          const currentComment = commentDesc ?? comment;
          const pattern = /<p><span>​<\/span><\/p>|<p><br><\/p>|<p><\/p>|<p><br><br><\/p>/g;
          if (isEmptyMessage(currentComment) && isEmpty(uploadedFiles)) {
            return rootStore.alertNotificationStore.setErrorMessage("Comment can't be empty.");
          }
          let processedDesc = currentComment;
          if (!isEmpty(processedDesc)) {
            while (pattern.test(processedDesc)) {
              processedDesc = processedDesc.replace(pattern, '');
            }
            if (processedDesc.match(/<br\s*>\s*<\/p>$/))
              processedDesc = processedDesc.replace(/<br\s*>\s*<\/p>$/, '</p>');
          }
          const userData = workspace?.users?.find((user) => user?.id === currentUser?.id);
          if ((!isEmpty(processedDesc) || uploadedFiles?.length > 0) && userData) {
            const id = nanoid();
            const processedCommentData = await processImageLinks(processedDesc);
            await handleCommentSubmission(processedCommentData, uploadedFiles, id);
          }
        } catch (e) {
          captureException(e);
        } finally {
          setLoading(false);
        }
      },
      [workspace?.users, currentUser?.id, processImageLinks, handleCommentSubmission, comment]
    );

    const resetCommentState = useCallback(() => {
      setEditComment(false);
      setReplyMessage({});
      if (editComment) {
        setUploadedFiles([]);
        setComment('');
        setCommentId(null);
      }
    }, [editComment, setComment, setUploadedFiles]);

    useImperativeHandle(
      ref,
      () => ({
        submitComment: onSendComment,
        resetCommentState
      }),
      [onSendComment, resetCommentState]
    );

    const onFileChange = useCallback(
      async (file: CommentFilesInterface[] | ((prevFiles: CommentFilesInterface[]) => CommentFilesInterface[])) => {
        const currentTaskId = window.location.pathname.split('/').pop();
        const finalFiles = typeof file === 'function' ? file(uploadedFiles || []) : file;
        setUploadedFiles(finalFiles);
        if (!isOpenComment) {
          const message = {
            id: currentTaskId,
            type: DRAFT_MESSAGE_TYPE.COMMENT,
            message: comment,
            file: finalFiles || []
          };
          await DraftMessageCacheService.getInstance()?.addBulk([message]);
          if ((isEmpty(comment) || comment === '<p></p>') && isEmpty(finalFiles)) {
            await DraftMessageCacheService.getInstance()?.removeBulk([currentTaskId]);
          }
        }
      },
      [comment, setUploadedFiles, uploadedFiles, isOpenComment]
    );

    const onEditCommentData = useCallback(
      async (commentData: CommentModel) => {
        setCommentId(commentData);
        setUploadedFiles(commentData?.Files || []);
        setReplyMessage(commentData?.reply || {});
        setComment(`${commentData?.comment || ''} <p><br /></p>`);
        setEditComment(true);
        setIsOpenComment(true);
      },
      [setComment, setIsOpenComment, setUploadedFiles]
    );

    return (
      <TaskActivity
        taskId={taskId}
        isKeyboardVisible={isKeyboardVisible}
        uploadedFiles={uploadedFiles}
        loading={loading}
        openEmoji={openEmoji}
        selectedMessage={selectedMessage}
        replyMessage={replyMessage}
        isOpenComment={isOpenComment}
        inlineScrollRef={inlineScrollRef}
        inlineRef={inlineRef}
        onEditComment={onEditCommentData}
        onDeleteComment={openDeleteCommentModal}
        figmaRefreshLoading={figmaRefreshLoading}
        onClickHandleRefreshFigma={onClickHandleRefreshFigma}
        onClickHandleRichtextClick={onClickHandleRichtextClick}
        handleActivityFileCard={handleActivityFileCard}
        attachmentPage={attachmentPage}
        onSendComment={onSendComment}
        handleOpenSnapModal={handleOpenSnapModal}
        openModal={() => setIsOpenComment(true)}
        setOpenEmoji={setOpenEmoji}
        setSelectedMessage={setSelectedMessage}
        setReplyMessage={setReplyMessage}
        onFileChange={onFileChange}
        setLoading={setLoading}
        emojiButtonRef={emojiButtonRef}
        buttonDropdownRef={buttonDropdownRef}
      />
    );
  })
);

export default TaskComments;
